// apps/provider/src/hooks/useBillingRuleEvaluation.js
// Hook that loads billing rules and evaluates them against encounter context.

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { evaluateBillingRules, buildCaseEvaluationContext, toCalendarDate } from '@prism-ehr/shared/utils/billingRuleEngine';
import { isMedicareCoverage, isPocExpirationViolation } from '../utils/pnDue';

/**
 * @param {Object} options
 * @param {Object} [options.encounter] - Current encounter with billing_codes (optional for case-level evaluation)
 * @param {Object} [options.linkedCase] - Linked case with encounter history
 * @param {Array} [options.caseEncounters] - All encounters for the linked case
 * @param {Object} [options.patient] - Patient with insurance info for payer matching
 * @param {string} [options.organizationId] - Org ID for rule loading
 * @param {string[]} [options.categoryFilter] - Only evaluate specific rule categories
 * @param {Array} [options.clinicalNotes] - Clinical notes for deriving progress note type
 * @param {Array} [options.plansOfCare] - POC records for deriving poc_certification_date
 * @returns {{ violations: Array, hasHardBlocks: boolean, hasAcknowledgeRequired: boolean, hasSoftWarnings: boolean, isLoading: boolean, evaluatedRuleCount: number, acknowledgments: Set, onAcknowledge: function, allAcknowledged: boolean }}
 */
export function useBillingRuleEvaluation({
  encounter,
  linkedCase,
  caseEncounters = [],
  clinicalNotes = [],
  patient,
  organizationId,
  categoryFilter,
  plansOfCare,
}) {
  const { user } = useUser();
  // Org setting: only SIGNED progress/reassessment notes satisfy a milestone
  // (settings.documentation.pn_due_signed_only; see buildCaseEvaluationContext)
  const signedOnly = !!user?.organization?.settings?.documentation?.pn_due_signed_only;
  // Org setting: poc_expiration violations only for Medicare/MA case coverage
  // (settings.documentation.poc_alerts_medicare_only; see utils/pnDue.js)
  const pocMedicareOnly = !!user?.organization?.settings?.documentation?.poc_alerts_medicare_only;
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [acknowledgments, setAcknowledgments] = useState(new Set());
  const loadedOrgRef = useRef(null);

  // Load rules once for the org
  useEffect(() => {
    if (!organizationId || loadedOrgRef.current === organizationId) return;
    let cancelled = false;

    const loadRules = async () => {
      setIsLoading(true);
      try {
        const result = await apiService.getBillingRules({
          organization_id: organizationId,
          is_active: true,
        });
        if (!cancelled) {
          setRules(result || []);
          loadedOrgRef.current = organizationId;
        }
      } catch (err) {
        console.error('Failed to load billing rules:', err);
        if (!cancelled) setRules([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadRules();
    return () => { cancelled = true; };
  }, [organizationId]);

  // Build evaluation context from encounter + case + visit history
  const evaluationContext = useMemo(() => {
    if (!encounter && !linkedCase) return null;

    const caseObj = {
      clinical_specialty: linkedCase?.clinical_specialty || encounter?.clinical_specialty,
      care_setting: linkedCase?.care_setting,
      id: linkedCase?.id || encounter?.case_id,
    };

    const baseContext = buildCaseEvaluationContext({
      caseObj,
      encounters: caseEncounters || [],
      clinicalNotes: clinicalNotes || [],
      plansOfCare,
      patient,
      milestoneRequiresSigned: signedOnly,
    });

    // For encounter-level evaluation, override visit number and add encounter details
    if (encounter) {
      const activeVisits = (caseEncounters || []).filter(e => e.status !== 'cancelled' && e.counts_toward_visit_total !== false);
      const sorted = [...activeVisits].sort((a, b) =>
        new Date(a.encounter_date) - new Date(b.encounter_date)
      );

      // Deduplicate by calendar date for visit numbering
      const uniqueDates = [...new Set(sorted.map(e => toCalendarDate(e.encounter_date)))];
      const encounterDate = encounter.encounter_date ? toCalendarDate(encounter.encounter_date) : null;
      const dateIndex = encounterDate ? uniqueDates.indexOf(encounterDate) : -1;
      const currentVisitNum = dateIndex >= 0 ? dateIndex + 1 : uniqueDates.length;

      // Build note_type lookup from clinical notes — preserve milestone types.
      // Note mode (hasNoteData) is decided on ANY typed note; the milestone
      // map only admits signed notes for signed-only orgs (mirrors
      // buildCaseEvaluationContext).
      const MILESTONE_NOTE_TYPES = ['progress_note', 'reassessment'];
      const noteTypeMap = {};
      let hasNoteData = false;
      for (const note of (clinicalNotes || [])) {
        if (note.encounter_id && note.note_type) {
          hasNoteData = true;
          if (signedOnly && note.signed !== true) continue;
          const key = String(note.encounter_id);
          const existing = noteTypeMap[key];
          if (!existing || !MILESTONE_NOTE_TYPES.includes(existing)) {
            noteTypeMap[key] = note.note_type;
          }
        }
      }

      // Recalculate last progress note excluding current encounter
      let lastProgressNoteDate = null;
      let lastProgressNoteVisit = null;
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].id === encounter.id) continue;
        if (hasNoteData) {
          const noteType = noteTypeMap[String(sorted[i].id)];
          if (noteType === 'progress_note' || noteType === 'reassessment') {
            lastProgressNoteDate = new Date(sorted[i].encounter_date).getTime();
            lastProgressNoteVisit = uniqueDates.indexOf(toCalendarDate(sorted[i].encounter_date)) + 1;
            break;
          }
        } else {
          if (sorted[i].encounter_type === 'follow_up' || sorted[i].documentation_status === 'signed') {
            lastProgressNoteDate = new Date(sorted[i].encounter_date).getTime();
            lastProgressNoteVisit = uniqueDates.indexOf(toCalendarDate(sorted[i].encounter_date)) + 1;
            break;
          }
        }
      }

      // Fallback to first encounter date if no progress note found (for window_days).
      // Leave lastProgressNoteVisit null so visit milestone checks detect "no note done."
      if (lastProgressNoteDate === null && sorted.length > 0 && sorted[0].id !== encounter.id) {
        lastProgressNoteDate = new Date(sorted[0].encounter_date).getTime();
      }

      const prevEncounters = sorted.filter(e => e.id !== encounter.id);
      const lastEncounterDate = prevEncounters.length > 0
        ? new Date(prevEncounters[prevEncounters.length - 1].encounter_date).getTime()
        : null;

      return {
        ...baseContext,
        encounter: {
          clinical_specialty: encounter.clinical_specialty || linkedCase?.clinical_specialty,
          care_setting: linkedCase?.care_setting,
          billing_codes: encounter.billing_codes,
          encounter_date: encounter.encounter_date,
          case_id: encounter.case_id,
          cpt_entry_complete: encounter.cpt_entry_complete,
        },
        visit_history: {
          ...baseContext.visit_history,
          current_visit_number: currentVisitNum,
          last_progress_note_date: lastProgressNoteDate,
          last_progress_note_visit: lastProgressNoteVisit,
          last_encounter_date: lastEncounterDate,
        },
      };
    }

    return baseContext;
  }, [encounter, linkedCase, caseEncounters, clinicalNotes, patient, plansOfCare, signedOnly]);

  // Evaluate rules against context
  const result = useMemo(() => {
    if (!evaluationContext || rules.length === 0) {
      return {
        violations: [],
        has_hard_blocks: false,
        has_acknowledge_required: false,
        has_soft_warnings: false,
        evaluated_rule_count: 0,
      };
    }
    const evaluated = evaluateBillingRules(rules, evaluationContext, categoryFilter);
    if (!pocMedicareOnly || isMedicareCoverage(linkedCase, patient)) return evaluated;
    const violations = evaluated.violations.filter(v => !isPocExpirationViolation(v));
    if (violations.length === evaluated.violations.length) return evaluated;
    return {
      ...evaluated,
      violations,
      has_hard_blocks: violations.some(v => v.enforcement_level === 'hard_block'),
      has_acknowledge_required: violations.some(v => v.enforcement_level === 'acknowledge_required'),
      has_soft_warnings: violations.some(v => v.enforcement_level === 'soft_warning'),
    };
  }, [rules, evaluationContext, categoryFilter, pocMedicareOnly, linkedCase, patient]);

  // Reset acknowledgments when violations change
  const violationKeyRef = useRef('');
  useEffect(() => {
    const ackViolations = result.violations
      .filter(v => v.enforcement_level === 'acknowledge_required')
      .map(v => v.rule_id)
      .sort()
      .join(',');
    if (ackViolations !== violationKeyRef.current) {
      violationKeyRef.current = ackViolations;
      setAcknowledgments(new Set());
    }
  }, [result.violations]);

  const onAcknowledge = useCallback((ruleId) => {
    setAcknowledgments(prev => {
      const next = new Set(prev);
      if (next.has(ruleId)) {
        next.delete(ruleId);
      } else {
        next.add(ruleId);
      }
      return next;
    });
  }, []);

  const ackViolations = result.violations.filter(v => v.enforcement_level === 'acknowledge_required');
  const allAcknowledged = ackViolations.length === 0 || ackViolations.every(v => acknowledgments.has(v.rule_id));

  return {
    violations: result.violations,
    hasHardBlocks: result.has_hard_blocks,
    hasAcknowledgeRequired: result.has_acknowledge_required,
    hasSoftWarnings: result.has_soft_warnings,
    evaluatedRuleCount: result.evaluated_rule_count,
    isLoading,
    acknowledgments,
    onAcknowledge,
    allAcknowledged,
  };
}
